import { Link, useParams } from "@tanstack/react-router";
import { Check, Package, Truck, Mail, ArrowRight } from "lucide-react";

const steps = [
  { icon: Check, label: "Potvrda", detail: "Narudžba zaprimljena", done: true },
  { icon: Package, label: "Pakovanje", detail: "Pripremamo artikle", done: false },
  { icon: Truck, label: "Dostava", detail: "Na putu do Vas", done: false },
];

export default function OrderConfirmation() {
  const { orderId } = useParams({ from: "/order-confirmation/$orderId" });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:py-20">
        <div
          className="w-full max-w-md"
          style={{ animation: "conf-appear 0.7s cubic-bezier(0.16, 1, 0.3, 1) both" }}
        >
          {/* Success icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div
                className="size-20 rounded-full bg-emerald-50 flex items-center justify-center"
                style={{ animation: "conf-ring 0.6s ease-out 0.2s both" }}
              >
                <div
                  className="size-12 rounded-full bg-emerald-500 flex items-center justify-center"
                  style={{ animation: "conf-check 0.4s ease-out 0.5s both" }}
                >
                  <Check className="size-6 text-white" strokeWidth={3} />
                </div>
              </div>
              {/* Pulse ring */}
              <div
                className="absolute inset-0 rounded-full border-2 border-emerald-200"
                style={{ animation: "conf-pulse 1.5s ease-out 0.8s both" }}
              />
            </div>
          </div>

          {/* Title + order number */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-[26px] font-semibold tracking-tight mb-2">
              Narudžba zaprimljena!
            </h1>
            <div className="inline-flex items-center gap-2 bg-foreground/[0.04] rounded-full px-4 py-1.5">
              <span className="text-sm text-muted-foreground">Narudžba</span>
              <span className="text-sm font-semibold tabular-nums">#{orderId}</span>
            </div>
          </div>

          {/* Info cards */}
          <div className="space-y-3 mb-8">
            {/* Payment */}
            <div className="bg-white rounded-xl border border-border/60 px-4 py-3.5 flex items-start gap-3">
              <div className="size-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-base">💰</span>
              </div>
              <div>
                <p className="text-sm font-medium">Plaćanje pouzećem</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Platite prilikom preuzimanja paketa od kurira ili na dogovorenoj adresi.
                </p>
              </div>
            </div>

            {/* Email notification */}
            <div className="bg-white rounded-xl border border-border/60 px-4 py-3.5 flex items-start gap-3">
              <div className="size-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0 mt-0.5">
                <Mail className="size-4.5 text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Email obavijest</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Kada Vaša narudžba bude poslana ili spremna za preuzimanje, dobićete email obavijest.
                </p>
              </div>
            </div>
          </div>

          {/* Timeline — what happens next */}
          <div className="bg-white rounded-xl border border-border/60 px-5 py-5 mb-8">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">
              Šta dalje?
            </p>
            <div className="flex items-start">
              {steps.map((step, i) => {
                const Icon = step.icon;
                const isLast = i === steps.length - 1;
                return (
                  <div key={step.label} className="flex-1 flex flex-col items-center text-center relative">
                    {/* Connector line */}
                    {!isLast && (
                      <div
                        className="absolute top-4 left-[calc(50%+16px)] right-[calc(-50%+16px)] h-px"
                        style={{
                          background: step.done
                            ? "#10b981"
                            : "linear-gradient(to right, var(--border), var(--border))",
                        }}
                      />
                    )}
                    {/* Step circle */}
                    <div
                      className={`relative z-10 size-8 rounded-full flex items-center justify-center mb-2 ${
                        step.done
                          ? "bg-emerald-500 text-white"
                          : "bg-muted/60 text-muted-foreground"
                      }`}
                    >
                      <Icon className="size-4" strokeWidth={step.done ? 2.5 : 2} />
                    </div>
                    <p className={`text-xs font-medium ${step.done ? "text-foreground" : "text-muted-foreground"}`}>
                      {step.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 hidden sm:block">
                      {step.detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Link
              to="/"
              className="group w-full bg-foreground text-white rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-foreground/90 transition-colors"
            >
              Nastavi kupovinu
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-[11px] text-muted-foreground/40 tracking-wide">
          Hvala što kupujete kod nas
        </p>
      </footer>

      <style>{`
        @keyframes conf-appear {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes conf-ring {
          from { transform: scale(0.6); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes conf-check {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        @keyframes conf-pulse {
          from { transform: scale(1); opacity: 0.6; }
          to { transform: scale(1.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
